import {Component, effect, inject, signal, WritableSignal} from '@angular/core';
import {NgClass, NgComponentOutlet, NgStyle} from "@angular/common";
import {NavigationStart, Router, RouterLink} from "@angular/router";
import {Tooltip} from "primeng/tooltip";
import {BsLibraryIconComponent} from "../../icons/bs-library-icon.component";
import {BsBuildKitIconComponent} from "../../icons/bs-build-kit-icon.component";
import {BsProjectIconComponent} from "../../icons/bs-project-icon.component";
import {BsCadIconComponent} from "../../icons/bs-cad-icon.component";
import {BsDrawingIconComponent} from "../../icons/bs-drawing-icon.component";
import {BsReportIconComponent} from "../../icons/bs-report-icon.component";
import {BsSwLogoComponent} from "../../icons/bs-sw-logo.component";
import {Role} from "../../models/role.model";
import {Permission} from "../../models/role-permission.model";
import {selectTenantRoleData} from "../../state/auth/auth.selectors";
import {Store} from "@ngrx/store";
import {BsUserManagementIconComponent} from "../../icons/bs-user-management-icon.component";
import {BsAddinIconComponent} from "../../icons/bs-addin-icon.component";

@Component({
  selector: 'bs-side-navigation-menu',
  imports: [
    NgClass,
    RouterLink,
    Tooltip,
    NgComponentOutlet,
    BsSwLogoComponent,
    NgStyle
  ],
  templateUrl: './bs-side-navigation-menu.component.html',
  styleUrl: './bs-side-navigation-menu.component.css',
  standalone: true,
})
export class BsSideNavigationMenuComponent {
  private store = inject(Store);
  private router = inject(Router);
  selectedRoute = signal('');

  ngOnInit(){
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.selectedRoute.set(event.url.split('/')[1] ?? '');
      }
    });
  }

  items: WritableSignal<any> = signal([
    {
      label: 'Projects',
      routerLink: 'projects',
      icon: BsProjectIconComponent
    },
    {
      label: 'CAD Data',
      routerLink: 'cad-data',
      icon: BsCadIconComponent
    },
    {
      label: 'Drawings',
      routerLink: 'drawings',
      icon: BsDrawingIconComponent

    },
    {
      label: 'Build Kits',
      routerLink: 'build-kit',
      icon: BsBuildKitIconComponent
    },
    {
      label: 'Reports',
      routerLink: 'reports',
      icon: BsReportIconComponent
    },
    {
      label: 'Library',
      routerLink: 'library',
      icon: BsLibraryIconComponent,
    },
    {
      label: 'Add-in',
      routerLink: 'add-in',
      icon: BsAddinIconComponent,
    },
  ]);

  tenantRoleData = this.store.selectSignal(selectTenantRoleData);
  isDisabled(item: any): boolean {
    const tenant = this.tenantRoleData()?.tenant;
    return !!tenant && !tenant.webappAccess && item?.routerLink !== 'add-in';
  }

  constructor() {
    effect(() => {
      if (this.tenantRoleData() && !!this.tenantRoleData()?.role) {
        this.createUserManagementMenu(this.tenantRoleData()?.role as Role)
      }
    });
  }

  createUserManagementMenu(role: Role) {
    var anyUserPermission = false;
    var anyUserInvitePermission = false;
    var anyRolePermission = false;
    const userManagementItems: any[] = [];
    if (!role.isAdmin) {
      for (const rolePermission of role?.rolePermissions) {
        if (rolePermission.permission === Permission.User) {
          anyUserPermission = true;
        }
        if (rolePermission.permission === Permission.UserInvite) {
          anyUserInvitePermission = true;
        }
        if (rolePermission.permission === Permission.Role) {
          anyRolePermission = true;
        }

        if (anyUserPermission || anyUserInvitePermission || anyRolePermission) break;
      }

      if (!anyUserPermission && !anyUserInvitePermission && !anyRolePermission) {
        return;
      }
    } else {
      anyUserPermission = true;
      anyUserInvitePermission = true;
      anyRolePermission = true;
    }

    if (anyRolePermission || anyUserPermission || anyUserInvitePermission){
      this.items.update(x => {
        if (x.some((item: any) => item.routerLink === 'user-management')) {
          return x;
        }
        const usersItem = {
          label: 'Users',
          items: userManagementItems,
          routerLink: 'user-management',
          icon: BsUserManagementIconComponent
        };
        const addInIndex = x.findIndex((item: any) => item.routerLink === 'add-in');
        if (addInIndex === -1) {
          return [...x, usersItem];
        }
        const next = [...x];
        next.splice(addInIndex, 0, usersItem);
        return next
      });
    }
  }

}
